import { type AuthorInfo, getCategoryAndSubcategory, getImageUrl } from "@serophin/nottldr-shared";
import { AuthorProfileImage } from "../author/AuthorProfileImage";
import { FollowButton } from "./FollowButton";
import { ShareAuthorButton } from "./ShareAuthorButton";

const plural = (count: number, one: string, many: string): string => `${count} ${count === 1 ? one : many}`;

const HeroImage = ({ author }: { author: AuthorInfo }) =>
  author.profileImage?.src ? (
    <img
      src={getImageUrl(author.profileImage.src, author.alias)}
      alt={author.profileImage.alt || `${author.alias} profile`}
      class="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
      loading="lazy"
    />
  ) : (
    <div
      class="absolute inset-0 transition-transform duration-300 group-hover:scale-110"
      style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}
    />
  );

const HeroText = ({ author }: { author: AuthorInfo }) => (
  <div class="z-20 absolute bottom-0 left-0 right-0 p-4">
    <div class="flex items-center gap-2 mb-2">
      <AuthorProfileImage author={author} size="sm" />
      <div class="flex-1 min-w-0 flex items-center gap-2">
        <h2 class="font-bold text-xl text-white">{author.alias}</h2>
      </div>
    </div>
    <p class="text-white/90 text-xs italic mb-2">{author.tagline}</p>
    <p class="text-white/90 text-xs">
      {plural(author.publishedArticleCount || 0, "article", "articles")} · {plural(author.followerCount || 0, "follower", "followers")}
    </p>
  </div>
);

const Hero = ({ author }: { author: AuthorInfo }) => (
  <div class="relative h-64 [clip-path:inset(0_round_0.75rem_0.75rem_0_0)]">
    <HeroImage author={author} />
    <div class="absolute inset-0 z-10 bg-gradient-to-t from-stone-800/80" />
    <div class="absolute top-4 left-4">
      <span class="bg-black/50 px-2 py-1 rounded-full text-xs font-medium backdrop-blur-sm text-white">
        {getCategoryAndSubcategory(author.expertise.category, author.expertise.subcategory).subcategory.name}
      </span>
    </div>
    <div class="absolute top-3 right-2 flex items-center gap-1 z-20">
      <ShareAuthorButton author={author} />
      <FollowButton alias={author.alias} />
    </div>
    <HeroText author={author} />
  </div>
);

const SUBJECT =
  "inline-block text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/30 px-2 py-0.5 rounded border border-gray-200 dark:border-gray-700";

export const AuthorCard = ({ author }: { author: AuthorInfo }) => (
  <a href={`/authors/${author.alias}`} class="block" data-test-click="author-card" data-test-author={author.alias}>
    <div class="group bg-white dark:bg-neutral-800 rounded-xl shadow-md shadow-gray-500/30 p-3 text-left">
      <div class="relative -m-3 mb-0">
        <Hero author={author} />
        <div class="p-3">
          <p class="text-gray-600 dark:text-gray-300 text-sm mb-3">{author.mission}</p>
          <div class="flex flex-wrap gap-1 border-t border-gray-200 dark:border-gray-700 pt-3">
            {author.expertise.subjects.map((subject) => (
              <span key={subject} class={SUBJECT}>
                {subject}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  </a>
);
